import { clientStorage } from "./clientStorage.js";

export const OUTLOOK_BILLING_FORM_TYPE = "lacw_outlook_billing";
export const OUTLOOK_BILLING_BRAND = "admin";
export const OUTLOOK_BILLING_ADMIN_READ_BRAND = "all";
export const OUTLOOK_BILLING_LOCAL_KEY = "lacw_outlook_billing_intake";

const clean = (value) => String(value || "").trim();

const toAustralianDate = (value) => {
  const match = clean(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : clean(value);
};

const fundingToGrantType = {
  "Legal Aid": "VLA",
  LACW: "LACW",
  Private: "Private",
  "Pro Bono": "Pro Bono",
};

function inferAtlasClaimType(appearanceType, claimable) {
  if (!claimable) return "";
  const type = clean(appearanceType).toLowerCase();
  if (type.includes("contest")) return "Contest hearing appearance";
  if (type.includes("sentence")) return "Appearance on sentence or adjournment";
  if (type.includes("bail")) return "Bail appearance fee";
  if (type.includes("committal")) return "Committal mention / case conference appearance";
  if (type.includes("plea")) return "Plea — appearance fee (first day)";
  if (type.includes("trial")) return "Trial — appearance fee (first day)";
  if (type.includes("mention")) return "Daily appearance fee";
  return "";
}

function initialsFromName(value) {
  return clean(value)
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 4)
    .toUpperCase();
}

function billingNotes(appearance) {
  return [
    appearance.fileNumber ? `Matter: ${clean(appearance.fileNumber)}` : "",
    appearance.grantOfAidNumber ? `Grant of aid: ${clean(appearance.grantOfAidNumber)}` : "",
    appearance.listingLocation ? `Listed at: ${clean(appearance.listingLocation)}` : "",
    appearance.matterToBeBriefed ? "Matter flagged to be briefed." : "",
    clean(appearance.notes),
  ].filter(Boolean).join("\n");
}

export function buildLacwBillingEntry(appearance, options = {}) {
  const fundingType = clean(appearance?.fundingType);
  const claimable = fundingType === "Legal Aid";
  const sourceId = clean(options.sourceId)
    || clean(appearance?.actionstepMatterId)
    || [appearance?.clientName, appearance?.date, appearance?.appearanceType].map(clean).join("|");

  return {
    id: `outlook_${sourceId.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "").slice(0, 80) || "appearance"}`,
    source_id: sourceId,
    source: "outlook",
    date: toAustralianDate(appearance?.date),
    client_name: clean(appearance?.clientName),
    grant_type: fundingToGrantType[fundingType] || fundingType,
    court: clean(appearance?.court),
    appearance_type: clean(appearance?.appearanceType),
    lawyer_initials: initialsFromName(options.lawyerName),
    counsel_briefed: "",
    outcome: clean(appearance?.outcome) || "Further information required.",
    next_date: toAustralianDate(appearance?.nextCourtDate),
    claimable,
    atlas_claim_type: inferAtlasClaimType(appearance?.appearanceType, claimable),
    notes: billingNotes(appearance || {}),
  };
}

export function buildLacwBillingDraft(appearance, options = {}) {
  const entry = buildLacwBillingEntry(appearance, options);
  const now = options.now || new Date().toISOString();
  const id = options.id || globalThis.crypto?.randomUUID?.();

  return {
    ...(id ? { id } : {}),
    form_type: OUTLOOK_BILLING_FORM_TYPE,
    draft_name: `${entry.client_name || "Client"} — ${entry.date || "date pending"}`,
    brand: OUTLOOK_BILLING_BRAND,
    form_data: {
      status: "pending_review",
      source: "outlook",
      submitted_at: now,
      entry,
    },
  };
}

export function getPortalRequestHeaders() {
  const token = clientStorage.getItem("clsp_session_token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function submitOutlookBillingDraft(draft, fetchImpl = globalThis.fetch) {
  const response = await fetchImpl("/api/vals-drafts", {
    method: "POST",
    credentials: "include",
    headers: getPortalRequestHeaders(),
    body: JSON.stringify(draft),
  });
  if (!response.ok) {
    throw new Error(`LACW billing intake returned ${response.status}`);
  }
  return response.json();
}

export async function upsertOutlookBillingDraft(draft, fetchImpl = globalThis.fetch) {
  const sourceId = clean(draft?.form_data?.entry?.source_id);
  if (sourceId) {
    const params = new URLSearchParams({
      brand: draft.brand || OUTLOOK_BILLING_BRAND,
      form_type: OUTLOOK_BILLING_FORM_TYPE,
    });
    const response = await fetchImpl(`/api/vals-drafts?${params}`, {
      credentials: "include",
      headers: getPortalRequestHeaders(),
    });
    if (!response.ok) throw new Error(`LACW billing intake lookup returned ${response.status}`);
    const drafts = await response.json();
    const existing = Array.isArray(drafts)
      ? drafts.find((item) => clean(item?.form_data?.entry?.source_id) === sourceId)
      : null;
    if (existing?.id) draft = { ...draft, id: existing.id, created_at: existing.created_at };
  }
  return submitOutlookBillingDraft(draft, fetchImpl);
}

export function readLocalOutlookBillingIntake() {
  try {
    const drafts = JSON.parse(clientStorage.getItem(OUTLOOK_BILLING_LOCAL_KEY) || "[]");
    return Array.isArray(drafts) ? drafts : [];
  } catch {
    return [];
  }
}

export function saveLocalOutlookBillingDraft(draft) {
  const drafts = readLocalOutlookBillingIntake();
  const saved = {
    ...draft,
    id: draft.id || `demo_outlook_${Date.now()}`,
    created_at: draft.created_at || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  clientStorage.setItem(OUTLOOK_BILLING_LOCAL_KEY, JSON.stringify([saved, ...drafts]));
  return saved;
}

export function removeLocalOutlookBillingDraft(id) {
  const next = readLocalOutlookBillingIntake().filter((draft) => draft.id !== id);
  clientStorage.setItem(OUTLOOK_BILLING_LOCAL_KEY, JSON.stringify(next));
  return next;
}
